import { Typography } from '@material-ui/core';
import { AttachFile, Cancel } from '@material-ui/icons';
import { errorMessages } from 'common/errorMessages.strings';
import { BaseDocumentResponse } from 'common/genericTypes';
import { MdSpace, TextButton } from 'm2m.components';
import React, { Fragment, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

export interface Attachment {
  id: string;
  file?: File;
  remoteFileInfo?: BaseDocumentResponse;
}

interface UploadProps {
  fileId?: string;
  disabled?: boolean;
  disableRemove?: boolean;
  inputAttachments?: Attachment[];
  onChange?(attachments: Attachment[]): void;
  onDelete?(id: string): void;
  onError?(error: any): void;
  multipleFiles?: boolean;
  aditamento?: boolean;
  overrideAcceptableFileTypes?: Array<AcceptableFileTypes>;
  dataTestId?: string;
}

export enum AcceptableFileTypes {
  ImageJpeg = 'image/jpeg',
  ImagePng = 'image/png',
  AppWord = 'application/msword',
  AppWordGeneric = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  AppExcel = 'application/vnd.ms-excel',
  AppExcelGeneric = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  AppPdf = 'application/pdf',
  Txt = 'text/plain',
  Zip = 'application/zip',
  XZipCompressed = 'application/x-zip-compressed',
  Html = 'text/html',
  Email = 'message/rfc822',
}

export const defaultAcceptableFileTypes = [
  AcceptableFileTypes.ImageJpeg,
  AcceptableFileTypes.ImagePng,
  AcceptableFileTypes.AppWord,
  AcceptableFileTypes.AppWordGeneric,
  AcceptableFileTypes.AppExcel,
  AcceptableFileTypes.AppExcelGeneric,
  AcceptableFileTypes.AppPdf,
  AcceptableFileTypes.Txt,
  AcceptableFileTypes.Zip,
  AcceptableFileTypes.XZipCompressed,
  AcceptableFileTypes.Html,
  AcceptableFileTypes.Email,
];

const UploadDocs: React.FC<UploadProps> = (props) => {
  const { inputAttachments, overrideAcceptableFileTypes, disableRemove } = props;
  const [highlight, setHightlight] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<Array<Attachment>>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptedFileTypes = !!overrideAcceptableFileTypes?.length
    ? overrideAcceptableFileTypes
    : defaultAcceptableFileTypes;

  const isValidFileType = (files: File[]): boolean => {
    return acceptedFileTypes.some((type) => files.some((file) => file?.type === type));
  };

  const handleFileTypeError = () => {
    props.onError?.(errorMessages.validation.upload.invalidExtension);
    setHightlight(false);
  };

  const openFileDialog = () => {
    if (props.disabled) return;
    inputRef?.current?.click();
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;

    let newFiles: File[] = [];
    if (props.multipleFiles) {
      newFiles = Array.from(event.dataTransfer.files);
    } else if (event.dataTransfer.files) {
      newFiles[0] = event.dataTransfer.files[0];
    }

    if (!newFiles.length) return;

    if (!isValidFileType(newFiles)) {
      handleFileTypeError();
      return;
    }

    const newAttachments = newFiles.map((newFile) => {
      return {
        id: props.fileId ?? uuid(),
        file: newFile,
      };
    });

    let updatedAttachments: Attachment[];
    if (props.fileId && attachments.some(({ id }) => id === props.fileId)) {
      updatedAttachments = attachments.filter((file) => file.id !== props.fileId).concat(newAttachments);
    } else {
      updatedAttachments = attachments.concat(newAttachments);
    }

    setAttachments(updatedAttachments);
    props.onChange?.(newAttachments);
    setHightlight(false);
    // Reseta o input para permitir selecionar os mesmos arquivos novamente
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;
    setHightlight(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (props.disabled) return;
    setHightlight(false);
  };

  const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) return;

    let newFiles: File[] = [];
    if (props.multipleFiles && event.target.files) {
      newFiles = Array.from(event.target.files);
    } else if (event.target.files) {
      newFiles[0] = event.target.files?.[0];
    }

    if (!newFiles.length) return;

    if (!isValidFileType(newFiles)) {
      handleFileTypeError();
      return;
    }

    const newAttachments = newFiles.map((newFile) => {
      return {
        id: props.fileId ?? uuid(),
        file: newFile,
      };
    });

    let updatedAttachments: Attachment[];
    if (props.fileId && attachments.some(({ id }) => id === props.fileId)) {
      updatedAttachments = attachments.filter((file) => file.id !== props.fileId).concat(newAttachments);
    } else {
      updatedAttachments = attachments.concat(newAttachments);
    }

    setAttachments(updatedAttachments);
    props.onChange?.(newAttachments);
    setHightlight(false);
    // Reseta o input para permitir selecionar os mesmos arquivos novamente
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDeleteClick = (id: string) => {
    const newFiles = attachments.filter((file) => file.id !== id);
    setAttachments(newFiles);
    props.onDelete?.(id);
  };

  return (
    <Fragment>
      <div
        onClick={openFileDialog}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          width: '100%',
          border: '1px dashed #D7D8D9',
          borderRadius: '8px',
          cursor: props.disabled ? 'default' : 'pointer',
          textAlign: 'center',
        }}
        data-testid={props.dataTestId}
      >
        <MdSpace />
        <AttachFile fontSize="small" />
        <Typography color="textSecondary" variant="body2">
          {highlight
            ? 'Solte para carregar os arquivos'
            : `Arraste e solte ou SELECIONE ${props.aditamento ? 'O TERMO DE ADITAMENTO' : 'OS DOCUMENTOS'} `}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept={acceptedFileTypes.join(',')}
          multiple
          style={{ display: 'none' }}
          onChange={onInputChange}
        />
        <MdSpace />
      </div>
      <MdSpace />
      <div>
        {attachments.concat(inputAttachments ?? []).map(({ id, file, remoteFileInfo }) => (
          <div key={id}>
            <Typography variant="subtitle1">{file?.name || remoteFileInfo?.fileName}</Typography>
            <TextButton disabled={disableRemove} onClick={() => onDeleteClick(id)} startIcon={<Cancel />}>
              {'Remover'}
            </TextButton>
          </div>
        ))}
      </div>
    </Fragment>
  );
};

export default UploadDocs;
